import { SyntheticEvent, useMemo } from "react"
import { useTranslation } from "react-i18next"

import Selector from "@/components/form/selector/Selector"
import Toggle from "@/components/form/toggle/Toggle"
import { usePdfDataContext } from "@/app/export/pdf/contexts/PdfDataContext"
import { usePdfBuilderStore, selectCurrentPageConfig } from "@/stores/pdfBuilder"
import {
  defaultRowsPerPage,
  maxRowsPerPage,
  MAX_METRICS_PER_PAGE_LANDSCAPE,
  MAX_METRICS_PER_PAGE_PORTRAIT,
} from "@/app/report/pdf/charts/BuilderDataTable"
import {
  getBuilderTableSections,
  resolveBuilderTableSection,
} from "@/app/report/common/sections/customTable"

import { DecimalPlacesSelector, MetricSelector } from "./SharedWidgets"
import type { ChartOptionsPanelProps } from "./types"

type SectionOption = {
  value: string
  label: string
}

/**
 * Table 의 컬럼 구성·그룹·계산 방식·정렬·요약 행은 Report Filters 에서 고른 리포트 템플릿의
 * Custom Table 설정을 그대로 따른다 (일감 #75, 3단계). 지표는 처음 추가될 때 그 표의 지표를 가져와
 * 선택된 상태로 두고, 여기서 더하거나 뺄 수 있다 (결정 2026-09-23). 그 외 항목을 바꾸려면
 * Settings > Report 의 템플릿에서 고친다. PDF 배치(분할·간격)는 빌더 고유 옵션이다.
 */
export default function TableOptions(props: ChartOptionsPanelProps) {
  const { t } = useTranslation()
  const { item, onDataSourceChange, onOptionChange, metricOptions, translateKey } = props
  const { reportTemplate } = usePdfDataContext()

  const templateSections = useMemo(() => getBuilderTableSections(reportTemplate), [reportTemplate])
  const currentSection = useMemo(
    () => resolveBuilderTableSection(templateSections, item.dataSource.sectionIndex),
    [templateSections, item.dataSource.sectionIndex],
  )
  const sectionOptions: SectionOption[] = useMemo(
    () =>
      templateSections.map((section, order) => ({
        value: String(section.index),
        label: section.settings.title
          ? t(section.settings.title)
          : `${t("table", "Table")} ${order + 1}`,
      })),
    [templateSections, t],
  )

  // 분할은 항상 수동 — 장당 행 수(0 = 행 분할 없음)·지표 수. 지표 수 상한은 용지 방향에 따라 세로 10·가로 14
  const isLandscape =
    usePdfBuilderStore((s) => selectCurrentPageConfig(s)?.orientation) === "landscape"
  const maxMetricsPerPage = isLandscape
    ? MAX_METRICS_PER_PAGE_LANDSCAPE
    : MAX_METRICS_PER_PAGE_PORTRAIT
  const rowsMax = maxRowsPerPage(isLandscape)
  const scaleOptions = [
    { value: "auto", label: t("layoutAuto", "Auto") },
    { value: "manual", label: t("layoutManual", "Manual") },
  ]
  const rowsPerPage = Math.min(item.options.rowsPerPage ?? defaultRowsPerPage(isLandscape), rowsMax)
  const setRowsPerPage = (value: number) =>
    onOptionChange("rowsPerPage", Math.max(0, Math.min(rowsMax, value)))
  const metricsPerPage = Math.min(
    item.options.metricsPerPage ?? maxMetricsPerPage,
    maxMetricsPerPage,
  )
  const setMetricsPerPage = (value: number) =>
    onOptionChange("metricsPerPage", Math.max(1, Math.min(maxMetricsPerPage, value)))

  return (
    <>
      <DecimalPlacesSelector
        value={item.options.decimalPlaces ?? 1}
        onChange={(value) => onOptionChange("decimalPlaces", value)}
      />
      {/* 어느 Custom Table 을 따를지 — 템플릿에 표가 둘 이상일 때만 고른다 */}
      {templateSections.length > 1 && (
        <div className="PropertyPanel__field">
          <Selector
            slotProps={{ popper: { className: "PropertyPanel__menu" } }}
            label={t("tableSection", "Table")}
            options={sectionOptions}
            value={
              sectionOptions.find((option) => option.value === String(currentSection?.index)) ||
              sectionOptions[0]
            }
            onChange={(_: SyntheticEvent, newVal: SectionOption | null) => {
              if (!newVal) {
                return
              }
              const nextIndex = Number(newVal.value)
              onDataSourceChange("sectionIndex", nextIndex)
              // 따르는 표가 바뀌면 지표도 그 표의 것으로 다시 가져온다
              const nextSection = templateSections.find((section) => section.index === nextIndex)
              if (nextSection) {
                onDataSourceChange("metrics", [...nextSection.settings.metrics])
              }
            }}
            placeholder={t("select")}
          />
        </div>
      )}
      {templateSections.length === 0 && (
        <div className="PropertyPanel__field">
          <span className="PropertyPanel__field-desc">
            {t("tableNotInTemplate", "The selected report template has no Table section")}
          </span>
        </div>
      )}

      {/* Metrics — 템플릿 표의 지표가 기본 선택, 아이템별로 더하고 뺀다 */}
      {currentSection && (
        <MetricSelector
          label={t("metrics", "Metrics")}
          selectedMetrics={item.dataSource.metrics ?? currentSection.settings.metrics}
          metricOptions={metricOptions}
          onAdd={(metricKey) =>
            onDataSourceChange("metrics", [
              ...(item.dataSource.metrics ?? currentSection.settings.metrics),
              metricKey,
            ])
          }
          onRemove={(metricKey) =>
            onDataSourceChange(
              "metrics",
              (item.dataSource.metrics ?? currentSection.settings.metrics).filter(
                (key) => key !== metricKey,
              ),
            )
          }
          translateKey={translateKey}
        />
      )}

      {/* 요약 행 — 템플릿에 있어도 아이템에서 Total·Average 각각 끌 수 있다 (2026-09-30 요청) */}
      <div className="PropertyPanel__field PropertyPanel__field--toggle">
        <Toggle
          checked={item.options.showTotalRow ?? true}
          onChange={(e) => onOptionChange("showTotalRow", e.target.checked)}
        />
        <label>{t("showTotalRow", "Show Total row")}</label>
      </div>
      <div className="PropertyPanel__field PropertyPanel__field--toggle">
        <Toggle
          checked={item.options.showAverageRow ?? true}
          onChange={(e) => onOptionChange("showAverageRow", e.target.checked)}
        />
        <label>{t("showAverageRow", "Show Average row")}</label>
      </div>

      {/* Layout — Auto(기본): 한 표로 길게(페이지 경계에서 행이 잘리지 않게), Manual: 장당 행·지표 수로 장마다 아이템 하나 (2026-09-29 요청) */}
      <div className="PropertyPanel__field">
        <Selector
          slotProps={{ popper: { className: "PropertyPanel__menu" } }}
          label={t("layout", "Layout")}
          options={scaleOptions}
          value={
            scaleOptions.find((o) => o.value === (item.options.tableScale ?? "auto")) ||
            scaleOptions[0]
          }
          onChange={(_: SyntheticEvent, newVal: { value: string; label: string } | null) => {
            if (newVal) {
              onOptionChange("tableScale", newVal.value)
            }
          }}
          placeholder={t("select")}
        />
      </div>

      {(item.options.tableScale ?? "auto") === "manual" && (
        <>
          <div className="PropertyPanel__field">
            <label>{t("rowsPerPage", "Rows per page")}</label>
            <div className="PropertyPanel__stepper">
              <input
                className="PropertyPanel__stepper-input"
                type="number"
                min={0}
                max={rowsMax}
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(parseInt(e.target.value) || 0)}
              />
              <button
                className="PropertyPanel__stepper-btn"
                onClick={() => setRowsPerPage(rowsPerPage - 1)}
                disabled={rowsPerPage <= 0}>
                −
              </button>
              <button
                className="PropertyPanel__stepper-btn"
                onClick={() => setRowsPerPage(rowsPerPage + 1)}
                disabled={rowsPerPage >= rowsMax}>
                +
              </button>
            </div>
            {/* 0 이면 행 분할 없음 — 항상 보이는 안내 대신 0일 때만 표기 (2026-09-28 결정) */}
            {rowsPerPage === 0 && (
              <span className="PropertyPanel__field-desc">{t("noSplit", "No split")}</span>
            )}
          </div>

          <div className="PropertyPanel__field">
            <label>{t("metricsPerPage", "Metrics per page")}</label>
            <div className="PropertyPanel__stepper">
              <input
                className="PropertyPanel__stepper-input"
                type="number"
                min={1}
                max={maxMetricsPerPage}
                value={metricsPerPage}
                onChange={(e) => setMetricsPerPage(parseInt(e.target.value) || 1)}
              />
              <button
                className="PropertyPanel__stepper-btn"
                onClick={() => setMetricsPerPage(metricsPerPage - 1)}
                disabled={metricsPerPage <= 1}>
                −
              </button>
              <button
                className="PropertyPanel__stepper-btn"
                onClick={() => setMetricsPerPage(metricsPerPage + 1)}
                disabled={metricsPerPage >= maxMetricsPerPage}>
                +
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}
